import React from 'react';
import {
  Card,
  Row,
  Col,
  Select,
  Input,
  Button,
  Space,
  Typography,
  Alert,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { SegmentCondition } from 'types/push.types';

const { Text } = Typography;

interface ConditionField {
  value: string;
  label: string;
  type: 'boolean' | 'string' | 'date';
}

const CONDITION_FIELDS: ConditionField[] = [
  { value: 'has_registration', label: 'Есть регистрация', type: 'boolean' },
  { value: 'has_deposit', label: 'Есть депозит', type: 'boolean' },
  { value: 'locale', label: 'Локаль', type: 'string' },
  { value: 'country', label: 'Страна', type: 'string' },
  { value: 'os', label: 'Операционная система', type: 'string' },
  { value: 'offer', label: 'Оффер', type: 'string' },
  { value: 'buyer_id', label: 'Байер', type: 'string' },
  { value: 'install_date', label: 'Дата установки', type: 'date' },
];

const OPERATORS = {
  boolean: [{ value: 'equals', label: 'Равно' }],
  string: [{ value: 'equals', label: 'Равно' }],
  date: [
    { value: 'greater_than', label: 'После' },
    { value: 'less_than', label: 'До' },
  ],
};

const OFFER_OPERATORS = [
  { value: 'contains', label: 'Содержит' },
  { value: 'not_contains', label: 'Не содержит' },
  { value: 'equals', label: 'Равно (точное совпадение)' },
];

const getOperators = (field: string, type: ConditionField['type']) =>
  field === 'offer' ? OFFER_OPERATORS : OPERATORS[type];

const OS_OPTIONS = ['android', 'ios', 'web'];

interface Props {
  conditions: SegmentCondition[];
  onChange: (conditions: SegmentCondition[]) => void;
  disabled?: boolean;
}

const SegmentConditionsBuilder: React.FC<Props> = ({
  conditions,
  onChange,
  disabled,
}) => {
  const addCondition = () => {
    onChange([
      ...conditions,
      { field: 'has_registration', operator: 'equals', value: true },
    ]);
  };

  const updateCondition = (
    index: number,
    field: keyof SegmentCondition,
    value: any,
  ) => {
    const next = [...conditions];
    if (field === 'field') {
      const fieldConfig = CONDITION_FIELDS.find(f => f.value === value);
      if (fieldConfig) {
                next[index] = {
          field: value as any,
          operator: getOperators(value, fieldConfig.type)[0].value as any,
          value: fieldConfig.type === 'boolean' ? true : '',
        };
      }
    } else {
      next[index] = { ...next[index], [field]: value };
    }
    onChange(next);
  };

  const removeCondition = (index: number) => {
    onChange(conditions.filter((_, i) => i !== index));
  };

  const getFieldConfig = (fieldValue: string): ConditionField | undefined => {
    return CONDITION_FIELDS.find(f => f.value === fieldValue);
  };

  const renderValueInput = (condition: SegmentCondition, index: number) => {
    const fieldConfig = getFieldConfig(condition.field);
    if (!fieldConfig) return null;

    switch (fieldConfig.type) {
      case 'boolean':
        return (
          <Select
            style={{ width: '100%' }}
            value={condition.value ? 'true' : 'false'}
            onChange={val => updateCondition(index, 'value', val === 'true')}
            disabled={disabled}
          >
            <Select.Option value="true">Да</Select.Option>
            <Select.Option value="false">Нет</Select.Option>
          </Select>
        );

        case 'date':
        return (
          <Input
            type="date"
            value={condition.value ? String(condition.value).slice(0, 10) : ''}
            onChange={e =>
              updateCondition(
                index,
                'value',
                e.target.value ? new Date(`${e.target.value}T00:00:00Z`).toISOString() : '',
              )
            }
            disabled={disabled}
          />
        );

      case 'string':
        if (condition.field === 'os') {
          return (
            <Select
              style={{ width: '100%' }}
              value={condition.value}
              onChange={val => updateCondition(index, 'value', val)}
              disabled={disabled}
            >
              {OS_OPTIONS.map(os => (
                <Select.Option key={os} value={os}>
                  {os.toUpperCase()}
                </Select.Option>
              ))}
            </Select>
          );
        }
        return (
          <Input
            placeholder={
              condition.field === 'offer'
                ? 'Например: oChilli или Project T'
                : condition.field === 'buyer_id'
                ? 'ID байера'
                : condition.field === 'country'
                ? 'Например: RU'
                : 'Введите значение'
            }
            value={condition.value}
            onChange={e => updateCondition(index, 'value', e.target.value)}
            disabled={disabled}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Card
      title="Условия"
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={addCondition}
          disabled={disabled}
        >
          Добавить условие
        </Button>
      }
    >
      {conditions.length === 0 ? (
        <Alert
          message="Условия не добавлены"
          description="Нажмите 'Добавить условие' для создания правил сегментации"
          type="info"
          showIcon
        />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {conditions.map((condition, index) => {
            const fieldConfig = getFieldConfig(condition.field);
            const operators = fieldConfig
              ? getOperators(condition.field, fieldConfig.type)
              : [];
            

            return (
              <Card key={index} size="small">
                <Row gutter={16} align="middle">
                  <Col xs={24} sm={8}>
                    <Space
                      direction="vertical"
                      style={{ width: '100%' }}
                      size={0}
                    >
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Поле
                      </Text>
                      <Select
                        style={{ width: '100%' }}
                        value={condition.field}
                        onChange={val => updateCondition(index, 'field', val)}
                        disabled={disabled}
                      >
                        {CONDITION_FIELDS.map(field => (
                          <Select.Option key={field.value} value={field.value}>
                            {field.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Space>
                  </Col>

                  <Col xs={24} sm={6}>
                    <Space
                      direction="vertical"
                      style={{ width: '100%' }}
                      size={0}
                    >
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Оператор
                      </Text>
                      <Select
                        style={{ width: '100%' }}
                        value={condition.operator}
                        onChange={val =>
                          updateCondition(index, 'operator', val)
                        }
                        disabled={disabled}
                      >
                        {operators.map(op => (
                          <Select.Option key={op.value} value={op.value}>
                            {op.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Space>
                  </Col>

                  <Col xs={24} sm={8}>
                    <Space
                      direction="vertical"
                      style={{ width: '100%' }}
                      size={0}
                    >
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Значение
                      </Text>
                      {renderValueInput(condition, index)}
                    </Space>
                  </Col>

                  <Col xs={24} sm={2} style={{ textAlign: 'right' }}>
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeCondition(index)}
                      disabled={disabled}
                    />
                  </Col>
                </Row>
              </Card>
            );
          })}
        </Space>
      )}
    </Card>
  );
};

export default SegmentConditionsBuilder;